import * as types from 'constants/ActionTypes';
import { custom, readWM, read } from 'utils/Api';
import { get, map, isEmpty, toNumber } from 'lodash';
import { getClockCardEntryTypes, getWorkingHoursToday, getXapValue } from '../sites/time/api';

const getYear = (year) => {
  const yearNow = new Date().getFullYear();
  const payYearCheck = new Date(yearNow, 4, 1).getTime() < Date.now() ? yearNow : yearNow - 1;
  const yearFixed = year || null;
  let payYear;
  if (yearFixed) {
    payYear = toNumber(yearFixed);
  } else {
    payYear = payYearCheck;
  }
  return payYear;
};

export const requestHoliday = () => ({
  type: types.REQUEST_HOLIDAY,
});

export const requestHolidaySuccess = (holidays, holidayCheckInfo, earnedHolidayInfo) => ({
  type: types.REQUEST_HOLIDAY_SUCCESS,
  holidays,
  holidayCheckInfo,
  earnedHolidayInfo
});

export const requestHolidayFailure = error => ({
  type: types.REQUEST_HOLIDAY_FAILURE,
  error,
});

export const fetch = () =>
  async (dispatch, getState) => {
    dispatch(requestHoliday());
    const fixedYear = get(await custom(getXapValue('EmployeeMasterHoliday.PayYear')), 'data.value[0].Value', null);
    const payYear = getYear(fixedYear);
    const {
      timeReducer: { entryTypes },
      loginReducer: { user: { EmployeeMasterID, DefaultEmployeeDetailID, token } },
      WebValuesReducer: { valuesByKey },
    } = getState();

    const config = {
      headers: { Authorization: `Bearer ${token}` },
      url: `/kjarni/api/v2/ClockCardEntries/Status(paywagecollectyear=${payYear},employeemasterid=${EmployeeMasterID})`,
    };

    const modelConfig = {
      entity: 'EmployeeDetailHoliday',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    };
    // We use inKjarniTime to know if customer is using viðvera. If using viðvera and check is not disabled
    // we need to get total registered vacation hours in current pay period to validate the holiday request.
    let periodFrom = get(await custom(getXapValue('Pay.PeriodFrom')), 'data.value[0].Value');
    const inKjarniTime = get(await custom(getWorkingHoursToday(DefaultEmployeeDetailID)), 'data.value[0].InKjarniTime') === true;
    const disableStatusCheck = get(valuesByKey, ['HolidayRights.DisableHolidayStatusCheck', 'Value']) === 'true';
    const isHolidayCheckDisabled = disableStatusCheck || !inKjarniTime;

    const vacationIds = [];
    if (!periodFrom) {
      periodFrom = '1';
    }
    if (!isHolidayCheckDisabled) {
      const clockTypes = !isEmpty(entryTypes) ? entryTypes : get(await custom(getClockCardEntryTypes), 'data.value', []);
      map(clockTypes, ({ ClockCardEntryTypeRule, ClockCardEntryTypeID }) => {
        if (ClockCardEntryTypeRule === 'Vacation') vacationIds.push(ClockCardEntryTypeID);
      });
    }
    const holidayCheckInfo = {
      isHolidayCheckDisabled,
      vacationIds,
      masterId: EmployeeMasterID,
      payYear
    };

    try {
      const data = get(await custom(config), 'data.value', []);

      let earnedHolidayInfo;

      const holidayStatus = await Promise.all(map(data, async (obj) => {
        const holidayModel = get(await readWM({ ...modelConfig, entityID: obj.EmployeeDetailID }), 'data');
        const {
          HrsPrMonth,
          PayHolidayDaysPerYear,
        } = holidayModel;
        const {
          WorkTimePerDay,
          Earned
        } = obj;
        const allowance = HrsPrMonth ? (HrsPrMonth * 12 * WorkTimePerDay) : (PayHolidayDaysPerYear * WorkTimePerDay);
        earnedHolidayInfo = {
          ...obj,
          HrsPrMonth,
          PayHolidayDaysPerYear,
          allowance
        };
        return {
          ...obj,
          HrsPrMonth,
          PayHolidayDaysPerYear,
        };
      }));

      dispatch(requestHolidaySuccess(holidayStatus, holidayCheckInfo, earnedHolidayInfo));
    } catch (error) {
      dispatch(requestHolidayFailure(error));
    }
  };
export const requestHolidayOverview = () => ({
  type: types.REQUEST_HOLIDAY_OVERVIEW,
});

export const requestHolidayOverviewSuccess = holidayOverview => ({
  type: types.REQUEST_HOLIDAY_OVERVIEW_SUCCESS,
  holidayOverview,
});

export const requestHolidayOverviewFailure = error => ({
  type: types.REQUEST_HOLIDAY_OVERVIEW_FAILURE,
  error,
});

export const fetchHolidayOverview = () =>
  async (dispatch, getState) => {
    try {
      const fixedYear = get(await custom(getXapValue('EmployeeMasterHoliday.PayYear')), 'data.value[0].Value', null);
      dispatch(requestHolidayOverview());
      const payYear = getYear(fixedYear);
      const {
        loginReducer: { user: { EmployeeMasterID, token } },
      } = getState();

      const config = {
        headers: { Authorization: `Bearer ${token}` },
        url: `/kjarni/api/v2/ClockCardEntries/OverviewCollect(paywagecollectyear=${payYear},employeemasterid=${EmployeeMasterID})`,
      };

      const overview = get(await custom(config), 'data.value[0]');
      dispatch(requestHolidayOverviewSuccess(overview));
    } catch (error) {
      dispatch(requestHolidayOverviewFailure(error));
    }
  };

const setHolidayInfo = holidayInfo => ({
  type: types.SET_HOLIDAY_INFO,
  holidayInfo,
});

const fetchHolidayByYearRequest = year => ({
  type: types.FETCH_HOLIDAY_BY_YEAR,
  year,
});
const fetchHolidayByYearSuccess = (holidays, holidaysOverview) => ({
  type: types.FETCH_HOLIDAY_BY_YEAR_SUCCESS,
  holidays,
  holidaysOverview,
});
const fetchHolidayByYearFailure = error => ({
  type: types.FETCH_HOLIDAY_BY_YEAR_FAILURE,
  error,
});

export const fetchHolidayByYear = year =>
  async (dispatch, getState) => {
    dispatch(fetchHolidayByYearRequest(year));
    const {
      loginReducer: { user: { EmployeeMasterID, token } },
    } = getState();

    const config = {
      headers: { Authorization: `Bearer ${token}` },
      url: `/kjarni/api/v2/ClockCardEntries/Status(paywagecollectyear=${year},employeemasterid=${EmployeeMasterID})`,
    };
    const modelConfig = {
      entity: 'EmployeeDetailHoliday',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    };
    const overviewConfig = {
      headers: { Authorization: `Bearer ${token}` },
      url: `/kjarni/api/v2/ClockCardEntries/OverviewCollect(paywagecollectyear=${year},employeemasterid=${EmployeeMasterID})`,
    };
    try {
      const data = get(await custom(config), 'data.value', []);
      const overview = get(await custom(overviewConfig), 'data.value[0]');
      const holidays = await Promise.all(map(data, async (obj) => {
        const holidayModel = get(await readWM({ ...modelConfig, entityID: obj.EmployeeDetailID }), 'data');

        return {
          ...obj,
          PayHolidayDaysPerYear: get(holidayModel, 'PayHolidayDaysPerYear'),
          HrsPrMonth: get(holidayModel, 'HrsPrMonth')
        };
      }));
      dispatch(fetchHolidayByYearSuccess(holidays, overview));
    } catch (error) {
      dispatch(fetchHolidayByYearFailure(error));
    }
  };


export const requestHolidayInfo = () =>
  (dispatch, getState) => {
    const {
      loginReducer: { user: { token } },
      EmployeeHolidayReducer: { holidayInfo },
      WebValuesReducer: { valuesByKey },
    } = getState();
    const templateConfig = valuesByKey['Info.EmployeeMasterHoliday.XapEmailTemplateID'];
    if (!holidayInfo &&
      templateConfig &&
      templateConfig.Value
    ) {
      const config = {
        entity: 'XapEmailTemplate',
        entityID: Number(templateConfig.Value),
        headers: { Authorization: `Bearer ${token}` },
      };
      return read(config)
        .then(({ data }) => {
          dispatch(setHolidayInfo(data));
        });
    }
    return null;
  };



// WEBPACK FOOTER //
// ./src/actions/EmployeeHoliday.js